import { useEffect, useState } from 'react';
import { api } from './api';
import { useAuth } from './auth';
import { tenantBrandStyle } from './branding';
import { Link, useLocation, useNavigate } from './router';

type TenantResolution = {
  organization?: {
    id: string;
    displayName: string;
    branding?: {
      logoUrl?: string | null;
      primaryColor?: string | null;
      accentColor?: string | null;
    };
  } | null;
};

export function NotFoundPage({ embedded = false }: { embedded?: boolean }) {
  const { me } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState<TenantResolution['organization']>(null);

  useEffect(() => {
    let active = true;
    void api<TenantResolution>('/tenancy/current')
      .then((result) => { if (active) setTenant(result.organization || null); })
      .catch(() => { if (active) setTenant(null); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = tenant?.displayName ? `Page not found | ${tenant.displayName}` : 'Page not found | Nuramend';
    return () => { document.title = previousTitle; };
  }, [tenant?.displayName]);

  const patientRoute = pathname === '/patient' || pathname.startsWith('/patient/');
  const platformRoute = pathname === '/admin' || pathname.startsWith('/admin/') || pathname.startsWith('/platform/');
  const platformUser = me?.user.platformRole !== 'NONE';
  const primaryTarget = platformUser || platformRoute ? '/admin' : me ? '/' : patientRoute ? '/patient/login' : '/login';
  const primaryLabel = platformUser || platformRoute ? 'Platform administration' : me ? 'Return to workspace' : patientRoute ? 'Patient portal sign in' : 'Go to sign in';
  const branding = tenant?.branding || {};
  const brandStyle = tenant ? tenantBrandStyle(branding.primaryColor, branding.accentColor) : undefined;
  const panelClass = embedded ? 'card not-found-panel' : 'auth-panel wide not-found-panel';

  const panel = <section className={panelClass} aria-labelledby="not-found-title">
    {tenant ? <div className={`tenant-login-brand${branding.logoUrl ? ' logo-only auth-original-logo' : ''}`}>
      {branding.logoUrl
        ? <img src={branding.logoUrl} alt={`${tenant.displayName} logo`} />
        : <><span className="tenant-login-mark">{String(tenant.displayName || 'N').slice(0, 1).toUpperCase()}</span><div><strong>{tenant.displayName}</strong><small>Secure practice workspace</small></div></>}
    </div> : <div className="brand auth-brand"><span className="brand-mark">N</span><span>Nuramend</span></div>}
    <div className="not-found-code" aria-hidden="true">404</div>
    <h1 id="not-found-title">Page not found</h1>
    <p>The address may be mistyped, the page may have moved, or the link may no longer be available.</p>
    <div className="not-found-path"><span>Requested path</span><code>{pathname}</code></div>
    <div className="button-row not-found-actions">
      <Link className="button primary" to={primaryTarget}>{primaryLabel}</Link>
      <button className="button" type="button" onClick={() => navigate(-1)}>Go back</button>
    </div>
    <small>{tenant ? 'Powered by Nuramend' : 'Nuramend secure application'}</small>
  </section>;

  if (embedded) return <div className="not-found-page">{panel}</div>;
  return <div className={`auth-page${tenant ? ' tenant-auth-page tenant-branded-surface' : ''}`} style={brandStyle as any}>{panel}</div>;
}
